import { Fragment, type ReactNode } from 'react';

/**
 * A small, safe Markdown renderer for legal documents (W21): headings (#, ##, ###), paragraphs,
 * bullet and numbered lists, block quotes, **bold**, _italic_, `code` and [links](/path). It
 * builds React elements only — no HTML is ever injected — and only allows http(s), mailto, tel
 * and relative links. Shared by Capsico OS, the apps and the site.
 */

export type MarkdownBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'banner'; text: string };

/** Splits Markdown into blocks. Exported for tests and for callers that need headings (TOC). */
export function parseMarkdown(source: string, bannerPrefix?: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let quote: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) {
      const text = paragraph.join(' ').trim();
      if (bannerPrefix && blocks.length === 0 && text.startsWith(bannerPrefix)) {
        blocks.push({ kind: 'banner', text });
      } else {
        blocks.push({ kind: 'paragraph', text });
      }
      paragraph = [];
    }
    if (list) {
      blocks.push({ kind: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }
    if (quote.length > 0) {
      blocks.push({ kind: 'quote', text: quote.join(' ').trim() });
      quote = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (trimmed === '') {
      flush();
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flush();
      blocks.push({
        kind: 'heading',
        level: (heading[1]?.length ?? 1) as 1 | 2 | 3,
        text: heading[2] ?? '',
      });
      continue;
    }
    const bullet = /^[-*]\s+(.*)$/.exec(trimmed);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(trimmed);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (paragraph.length > 0 || quote.length > 0 || (list && list.ordered !== ordered)) flush();
      if (!list) list = { ordered, items: [] };
      list.items.push((bullet?.[1] ?? numbered?.[1] ?? '').trim());
      continue;
    }
    if (trimmed.startsWith('>')) {
      if (paragraph.length > 0 || list) flush();
      quote.push(trimmed.replace(/^>\s?/, ''));
      continue;
    }
    if (list && /^\s{2,}/.test(raw)) {
      const items = list.items;
      items[items.length - 1] = `${items[items.length - 1] ?? ''} ${trimmed}`;
      continue;
    }
    if (list || quote.length > 0) flush();
    paragraph.push(trimmed);
  }
  flush();
  return blocks;
}

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/|#|\?)/i;

/** Inline formatting: [link](href), **bold**, _italic_ / *italic*, `code`. */
function inline(
  text: string,
  resolveHref: ((href: string) => string | null) | undefined,
  keyPrefix: string,
): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern =
    /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|(?:^|(?<=\W))_([^_]+)_(?=\W|$)|\*([^*]+)\*/g;
  let last = 0;
  let index = 0;
  for (const match of text.matchAll(pattern)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const key = `${keyPrefix}-${index}`;
    index += 1;
    if (match[1] !== undefined && match[2] !== undefined) {
      const resolved = resolveHref ? resolveHref(match[2]) : match[2];
      if (resolved && SAFE_HREF.test(resolved)) {
        const external = /^https?:\/\//i.test(resolved);
        out.push(
          <a
            key={key}
            href={resolved}
            className="font-medium text-info underline-offset-2 hover:underline"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {match[1]}
          </a>,
        );
      } else {
        out.push(<Fragment key={key}>{match[1]}</Fragment>);
      }
    } else if (match[3] !== undefined) {
      out.push(
        <strong key={key} className="font-semibold text-ink">
          {inline(match[3], resolveHref, key)}
        </strong>,
      );
    } else if (match[4] !== undefined) {
      out.push(
        <code key={key} className="rounded-control bg-surface-2 px-1 font-mono text-sm">
          {match[4]}
        </code>,
      );
    } else {
      out.push(<em key={key}>{match[5] ?? match[6]}</em>);
    }
    last = at + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export interface MarkdownProps {
  source: string;
  /** First paragraph starting with this text renders as a warning callout (the draft banner). */
  bannerPrefix?: string;
  /** Rewrites link targets (e.g. relative site paths to absolute URLs in the apps). null = no link. */
  resolveHref?: (href: string) => string | null;
  className?: string;
  /** Size variant: "md" for pages, "sm" for sheets and previews. */
  size?: 'sm' | 'md';
}

/** Renders a legal document (or any trusted-format Markdown) with Capsico typography. */
export function Markdown({
  source,
  bannerPrefix,
  resolveHref,
  className = '',
  size = 'md',
}: MarkdownProps) {
  const blocks = parseMarkdown(source, bannerPrefix);
  const body = size === 'sm' ? 'text-sm leading-6' : 'text-base leading-7';
  return (
    <div className={`space-y-4 text-ink ${body} ${className}`}>
      {blocks.map((block, index) => {
        const key = `b${index}`;
        switch (block.kind) {
          case 'banner':
            return (
              <p
                key={key}
                role="note"
                className="rounded-card border border-warning/30 bg-warning-bg px-4 py-3 font-semibold text-warning"
              >
                {inline(block.text, resolveHref, key)}
              </p>
            );
          case 'heading': {
            const cls =
              block.level === 1
                ? 'text-2xl font-semibold leading-8'
                : block.level === 2
                  ? 'pt-2 text-lg font-semibold leading-7'
                  : 'text-base font-semibold leading-6';
            const Tag = block.level === 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3';
            return (
              <Tag key={key} className={cls}>
                {inline(block.text, resolveHref, key)}
              </Tag>
            );
          }
          case 'quote':
            return (
              <blockquote
                key={key}
                className="rounded-card border-l-4 border-brand bg-brand-soft px-4 py-3 text-ink-2"
              >
                {inline(block.text, resolveHref, key)}
              </blockquote>
            );
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag
                key={key}
                className={`space-y-1.5 pl-6 ${block.ordered ? 'list-decimal' : 'list-disc'} marker:text-ink-3`}
              >
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-${itemIndex}`}>
                    {inline(item, resolveHref, `${key}-${itemIndex}`)}
                  </li>
                ))}
              </Tag>
            );
          }
          default:
            return <p key={key}>{inline(block.text, resolveHref, key)}</p>;
        }
      })}
    </div>
  );
}
